// LoadGuideDialog.js - Guide loading interface dialog
import React from 'react';
import { X, Upload, FolderOpen, Loader, Plus, FileText, Files } from 'lucide-react';
import GuidesListing from '../GuidesListing';

const LoadGuideDialog = ({
  isOpen,
  onClose,
  onFileLoad,
  onLoadFromListing,
  onCreateNew,
  onImportFromTxt,
  isLoading,
  loadProgress,
  className = ''
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 overflow-auto">
      <div className="min-h-screen p-4">
        <div className={`max-w-6xl mx-auto bg-white rounded-lg shadow-xl ${className}`}>
          {/* Header */}
          <div className="flex justify-between items-center p-6 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <FolderOpen className="w-6 h-6 text-blue-600" />
              <h2 className="text-xl font-semibold text-gray-800">
                Load or Create Guide
              </h2>
            </div>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Loading State */}
          {isLoading && (
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center gap-3 mb-4">
                <Loader className="w-5 h-5 animate-spin text-blue-600" />
                <span className="text-gray-700">Loading guide...</span>
              </div>
              {loadProgress > 0 && (
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${loadProgress}%` }}
                  ></div>
                </div>
              )}
            </div>
          )}

          {/* Content */}
          <div className="p-6">
            {/* Action Options */}
            <div className="grid md:grid-cols-4 gap-6 mb-8">
              {/* Create New Guide */}
              <div className="text-center">
                <button
                  onClick={onCreateNew}
                  className="w-full p-6 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all group"
                >
                  <Plus className="w-12 h-12 mx-auto text-gray-400 group-hover:text-blue-500 mb-4" />
                  <h3 className="font-medium text-gray-800 group-hover:text-blue-700 mb-2">
                    Create New Guide
                  </h3>
                  <p className="text-sm text-gray-600">
                    Start building a guide from scratch
                  </p>
                </button>
              </div>

              {/* Load from File */}
              <div className="text-center">
                <label className="block w-full p-6 border-2 border-dashed border-gray-300 rounded-lg hover:border-green-500 hover:bg-green-50 transition-all group cursor-pointer">
                  <Upload className="w-12 h-12 mx-auto text-gray-400 group-hover:text-green-500 mb-4" />
                  <h3 className="font-medium text-gray-800 group-hover:text-green-700 mb-2">
                    Load from File
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Upload a JSON guide file
                  </p>
                  <input
                    type="file"
                    onChange={onFileLoad}
                    className="hidden"
                    accept=".json"
                  />
                  <span className="inline-block px-4 py-2 bg-gray-100 text-gray-700 rounded text-sm group-hover:bg-green-100 group-hover:text-green-700">
                    Browse Files
                  </span>
                </label>
              </div>

              {/* Load from Listing */}
              <div className="text-center">
                <div className="w-full p-6 border-2 border-gray-300 rounded-lg bg-gray-50">
                  <FileText className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                  <h3 className="font-medium text-gray-800 mb-2">
                    Load from Library
                  </h3>
                  <p className="text-sm text-gray-600">
                    Choose from existing guides below
                  </p>
                </div>
              </div>

              {/* Import from TXT files */}
              <div className="text-center">
                <label className="block w-full p-6 border-2 border-dashed border-gray-300 rounded-lg hover:border-purple-500 hover:bg-purple-50 transition-all group cursor-pointer">
                  <Files className="w-12 h-12 mx-auto text-gray-400 group-hover:text-purple-500 mb-4" />
                  <h3 className="font-medium text-gray-800 group-hover:text-purple-700 mb-2">
                    Import from TXT
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Upload <code className="bg-gray-100 px-1 rounded">01_Stop_Name.txt</code> files — one per stop
                  </p>
                  <input
                    type="file"
                    onChange={onImportFromTxt}
                    className="hidden"
                    accept=".txt"
                    multiple
                  />
                  <span className="inline-block px-4 py-2 bg-gray-100 text-gray-700 rounded text-sm group-hover:bg-purple-100 group-hover:text-purple-700">
                    Select TXT Files
                  </span>
                </label>
              </div>
            </div>

            {/* Guides Listing */}
            <div className="border-t border-gray-200 pt-6">
              <h3 className="text-lg font-medium text-gray-800 mb-4">
                Available Guides
              </h3>
              <div className="max-h-96 overflow-auto">
                <GuidesListing onLoadGuide={onLoadFromListing} />
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoadGuideDialog;